import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Loader2, AlertCircle, Sparkles, CircleHelp, X, Users, Filter } from 'lucide-react';
import { Button } from '../../../shared/components/ui/Button';
import AudienceStats from '../components/AudienceStats';
import AudienceSearch from '../components/AudienceSearch';
import UserTable from '../components/UserTable';
import UserProfileModal from '../components/UserProfileModal';
import SyncStatusBadge from '../../integrations/components/SyncStatusBadge';
import { useIntegracion, UsuarioSimple } from '../../../hooks/useIntegracion';

// UI format for user display
interface UIUser {
    id: string;
    name: string;
    email: string;
    ltv: number;
    orders: number;
    tokens: number;
    lastActive: string;
    lastPurchase?: string;
    status: string;
    intent: string;
    tags: string[];
    source: string;
    birthDate?: string;
    gender?: string;
}

const AudiencePage = () => {
    const navigate = useNavigate();
    const [selectedUser, setSelectedUser] = useState<UIUser | null>(null);
    const [users, setUsers] = useState<UIUser[]>([]);
    const [allUsers, setAllUsers] = useState<UIUser[]>([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [databaseValue, setDatabaseValue] = useState(0);
    const [potentialDatabaseValue, setPotentialDatabaseValue] = useState(0);
    const [potentialGrowthPercentage, setPotentialGrowthPercentage] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [lastSync, setLastSync] = useState(new Date().toISOString());
    const [aiExplanation, setAiExplanation] = useState<string | null>(null);
    const [showTutorial, setShowTutorial] = useState(false);

    // Use the new integration hook
    const { getUsuarios, searchAudience, loading, error } = useIntegracion();

    // Mapped user from Backend
    const mapBackendUserToUI = useCallback((user: UsuarioSimple & { totalPoints?: number, totalGastado?: number, userCategory?: string, discountPct?: number, birthDate?: string, gender?: string }): UIUser => ({
        id: user._id,
        name: user.name || 'Sin nombre',
        email: user.email,
        ltv: user.totalGastado || 0,
        orders: (user as any).cantidadPedidos || 0,
        tokens: user.totalPoints || 0, // Usar puntos reales
        lastPurchase: (user as any).lastPurchase || undefined,
        lastActive: user.lastLoginAt
            ? `Hace ${Math.floor((Date.now() - new Date(user.lastLoginAt).getTime()) / (1000 * 60 * 60 * 24))} días`
            : 'Nunca',
        status: user.isActive ? 'Activo' : 'Inactivo',
        // Inferir intención de compra desde pedidos acumulados
        intent: (() => {
            const orders = (user as any).cantidadPedidos || 0;
            if (orders >= 20) return 'High';
            if (orders >= 5) return 'Medium';
            return 'Low';
        })(),
        tags: [
            user.userCategory || 'Cliente',
            `${user.discountPct || 0}% OFF`
        ].filter(t => t !== '-' && t !== '0% OFF'),
        source: 'deenex-multi',
        birthDate: user.birthDate ? new Date(user.birthDate).toLocaleDateString() : undefined,
        gender: user.gender
    }), []);

    // Pagination State
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 50;

    // Initial Fetch (No pagination for MVP)
    const fetchUsers = useCallback(async (pageNum = 1) => {
        try {
            const result = await getUsuarios(pageNum, limit);
            if (result && result.data) {
                const uiUsers = result.data.map((u: any) => mapBackendUserToUI(u));
                setAllUsers(uiUsers);
                setUsers(uiUsers);
                setTotalUsers(result.total || uiUsers.length);
                setTotalPages(Math.ceil((result.total || uiUsers.length) / limit));
                setDatabaseValue(result.totalDatabaseValue || 0);
                setPotentialDatabaseValue(result.potentialDatabaseValue || 0);
                setPotentialGrowthPercentage(result.potentialGrowthPercentage || 0);
                setLastSync(new Date().toISOString());
                setAiExplanation(null);
            }
        } catch (err) {
            console.error('Error fetching audience:', err);
        }
    }, [getUsuarios, mapBackendUserToUI]);

    useEffect(() => {
        fetchUsers(page);
    }, [page]);

    const handleSearch = async (query: string) => {
        setSearchQuery(query);
        setAiExplanation(null);

        if (!query) {
            setUsers(allUsers);
            return;
        }

        // AI Search
        try {
            const result = await searchAudience(query);

            if (result && result.success) {
                // Success
                const uiUsers = result.data.map((u: any) => mapBackendUserToUI(u));
                setUsers(uiUsers);
                setTotalUsers(result.total || uiUsers.length);
                setDatabaseValue(result.totalDatabaseValue || 0);
                setPotentialDatabaseValue(result.potentialDatabaseValue || 0);
                setPotentialGrowthPercentage(result.potentialGrowthPercentage || 0);
                if (result.ai && result.ai.explanation) {
                    setAiExplanation(result.ai.explanation);
                }
            } else if (result && result.needsClarification) {
                // Clarification needed — mostramos inline en lugar de alert nativo
                setAiExplanation(
                    `🤔 La IA necesita más detalle para responder. ${result.question}`,
                );
                setUsers(allUsers); // restauramos lista completa para que no quede vacía
            } else {
                // Fallback
                console.warn('AI Search failed or empty');
                // Local filter for standard queries as fallback
                const lowerQuery = query.toLowerCase();
                const filtered = allUsers.filter(user =>
                    user.name.toLowerCase().includes(lowerQuery) ||
                    user.email.toLowerCase().includes(lowerQuery)
                );
                setUsers(filtered);
            }
        } catch (e) {
            console.error('Search error', e);
        }
    };

    const handleUserSelect = (user: any) => {
        setSelectedUser(user);
    };

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-7xl space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Audiencia en Vivo</h1>
                        {/* H05: si está "sincronizado" pero con 0 usuarios, mostramos
                            warning en lugar de "connected" — evita contradicción visual
                            de banner verde + empty state */}
                        <SyncStatusBadge
                          status={
                            error
                              ? 'error'
                              : totalUsers === 0 && !loading
                                ? 'warning'
                                : 'connected'
                          }
                          lastSync={lastSync}
                        />
                    </div>
                    <p className="text-slate-500 text-lg">Base de datos de clientes en tiempo real enriquecida con comportamientos de IA.</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant={showTutorial ? "primary" : "secondary"}
                        className="!py-2 !px-4 text-sm font-medium"
                        onClick={() => setShowTutorial(!showTutorial)}
                    >
                        {showTutorial ? <X size={16} /> : <CircleHelp size={16} />}
                        {showTutorial ? "Cerrar Tutorial" : "Tutorial"}
                    </Button>
                    <Button
                        variant="secondary"
                        className="!py-2 !px-4 text-sm font-medium"
                        onClick={() => fetchUsers()}
                        disabled={loading}
                    >
                        {loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                        {loading ? 'Sincronizando...' : 'Sincronizar ahora'}
                    </Button>
                </div>
            </div>

            {/* AI Search Bar */}
            <div className="space-y-2">
                <AudienceSearch
                    onSearch={handleSearch}
                    resultCount={searchQuery ? users.length : undefined}
                    loading={loading && !!searchQuery}
                    showTutorial={showTutorial}
                />
                {/* AI Explanation Badge + save as segment */}
                {aiExplanation && (
                    <div className="flex items-start justify-between gap-3 px-4 py-2 bg-violet-50 text-violet-700 rounded-lg border border-violet-100 text-sm animate-fade-in flex-wrap">
                        <div className="flex items-center gap-2">
                            <Sparkles size={14} className="flex-shrink-0" />
                            <span className="font-semibold">Filtro IA aplicado:</span>
                            <span>{aiExplanation}</span>
                        </div>
                        {users.length > 0 && (
                            <button
                                onClick={() => navigate("/segments/new/ai", {
                                    state: { initialMessage: searchQuery }
                                })}
                                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors whitespace-nowrap flex-shrink-0"
                            >
                                <Filter size={12} />
                                Guardar como segmento ({users.length})
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Stats Cards */}
            <AudienceStats
                totalUsers={totalUsers}
                databaseValue={databaseValue}
                potentialDatabaseValue={potentialDatabaseValue}
                potentialGrowthPercentage={potentialGrowthPercentage}
                showTutorial={showTutorial}
            />

            {/* Content States */}
            {loading && users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-100 shadow-sm">
                    <Loader2 className="animate-spin text-violet-500 mb-4" size={40} />
                    <p className="text-slate-500 font-medium">Consultando a tu IA de Audiencia...</p>
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-red-100 shadow-sm">
                    <AlertCircle className="text-red-500 mb-4" size={40} />
                    <p className="text-red-600 font-medium">{error}</p>
                    <Button variant="secondary" onClick={() => fetchUsers()} className="mt-4">Reintentar</Button>
                </div>
            ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 px-6 bg-white rounded-2xl border border-slate-100 shadow-sm text-center">
                    <div className="w-16 h-16 bg-violet-50 rounded-2xl flex items-center justify-center mb-4">
                        <Users className="text-violet-500" size={28} />
                    </div>
                    <p className="text-slate-900 font-semibold mb-2">
                        Todavía no hay clientes para mostrar
                    </p>
                    <p className="text-slate-500 text-sm max-w-md leading-relaxed mb-5">
                        Tu cuenta aún no está asociada a una marca de Deenex Multi
                        con clientes cargados. Una vez vinculada, la audiencia
                        aparecerá automáticamente acá.
                    </p>
                    <Button variant="secondary" onClick={() => fetchUsers()}>
                        Volver a intentar
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    <UserTable
                        users={users as any[]}
                        onSelectUser={handleUserSelect}
                        showTutorial={showTutorial}
                    />

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 bg-white border border-slate-100 rounded-b-2xl border-t-0">
                            <div className="text-sm text-slate-500">
                                Mostrando página <span className="font-medium">{page}</span> de <span className="font-medium">{totalPages}</span>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page === 1}
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                >
                                    Anterior
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                >
                                    Siguiente
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* User Profile Modal */}
            {selectedUser && (
                <UserProfileModal
                    user={selectedUser as any}
                    onClose={() => setSelectedUser(null)}
                />
            )}
            </div>
        </div>
    );
};

export default AudiencePage;
